<script setup lang="ts">
type Props = {
  availableRewards: Reward[];
  finishedsRewards: Reward[];
  currentType: string;
  isRedeemableRewardsEnabled: boolean;
};

const {
  availableRewards,
  finishedsRewards,
  currentType,
  isRedeemableRewardsEnabled,
} = defineProps<Props>();

type RewardTabId = 'available' | 'finisheds';

type RewardTab = {
  id: RewardTabId;
  name: string;
};

const modelValue = defineModel<RewardTabId>('modelValue', {
  required: true,
});

const { t } = useTexts();
const allRewardsTabs: RewardTab[] = [
  {
    id: 'available',
    name: t('rewards.available'),
  },
  {
    id: 'finisheds',
    name: t('rewards.finished'),
  },
];

const rewardsTabs = computed<RewardTab[]>(() => {
  if (!isRedeemableRewardsEnabled) {
    return allRewardsTabs.filter((tab) => tab.id === 'finisheds');
  }

  return allRewardsTabs;
});

const availableRewardsCount = computed<string>(() => {
  if (availableRewards.length >= 10) {
    return '9+';
  }

  return availableRewards.length.toString();
});

const finishedsRewardsCount = computed<string>(() => {
  if (finishedsRewards.length >= 10) {
    return '9+';
  }

  return finishedsRewards.length.toString();
});

const { fetchRewardsFromApi, isLoadingRewards, rewrdsPagination } =
  useRewards();

const currentTypeAvailable = computed<string>(() => {
  return rewrdsPagination.value.available.filter;
});

const currentTypeFinisheds = computed<string>(() => {
  return rewrdsPagination.value.finisheds.filter;
});

async function handleTabChange(status: RewardTabId) {
  modelValue.value = status;
  if (
    (status === 'available' && availableRewards.length === 0) ||
    currentType !== currentTypeAvailable.value
  ) {
    await fetchRewardsFromApi(status, currentType, 1);
  } else if (
    (status === 'finisheds' && finishedsRewards.length === 0) ||
    currentType !== currentTypeFinisheds.value
  ) {
    await fetchRewardsFromApi(status, currentType, 1);
  }
}

async function handleRefresh() {
  await fetchRewardsFromApi(modelValue.value, currentType, 1);
}
</script>

<template>
  <div :class="$style.rewardsTabsContainer">
    <div
      v-for="tab in rewardsTabs"
      :key="tab.id"
      :class="[
        $style.rewardTab,
        {
          [$style.rewardTabActive]: modelValue === tab.id,
        },
      ]"
      @click.prevent="handleTabChange(tab.id)"
    >
      <span>{{ tab.name }}</span>
      <span
        v-if="tab.id === 'available'"
        :class="$style.tabPill"
      >
        {{
          tab.id === 'available' ? availableRewardsCount : finishedsRewardsCount
        }}
      </span>
    </div>

    <div
      :class="[$style.rewardTab, $style.refreshButton]"
      @click="handleRefresh"
    >
      <NuxtIcon
        :class="[
          $style.refreshIcon,
          {
            [$style.refreshIconLoading]: isLoadingRewards,
          },
        ]"
        name="sync"
      />
    </div>
  </div>
</template>

<style module lang="postcss">
.rewardsTabsContainer {
  @apply flex items-stretch justify-between gap-2 w-full bg-texts/10 rounded-lg p-2;

  .rewardTab {
    @apply flex items-center gap-2 cursor-pointer flex-1 justify-center bg-texts/5 rounded-lg p-2 font-semibold text-xs md:text-sm border border-solid border-texts/5;

    &.rewardTabActive {
      @apply bg-button-bg/15 text-button-bg border-button-bg/10 brightness-110;
    }

    .tabPill {
      @apply text-[.6rem] bg-error text-white font-semibold rounded-full w-4 h-4 flex items-center justify-center leading-none text-center tracking-tighter;
    }

    &.refreshButton {
      @apply flex-none px-3;
    }
  }

  .refreshIcon {
    &.refreshIconLoading {
      @apply animate-spin;
    }
  }
}
</style>
